export default function Requirements() {
  return (
    <section id="requirements" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Insurance & Requirements
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Everything you need to know before renting. We keep it simple and straightforward.
          </p>
        </div>

        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Insurance Information */}
          <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-2xl p-8 md:p-10">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-14 h-14 flex items-center justify-center bg-teal-600 rounded-lg">
                <i className="ri-shield-check-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900">Insurance Coverage</h3>
            </div>

            <div className="space-y-6">
              <div>
                <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                  <i className="ri-check-line text-teal-600"></i>
                  Basic Coverage Included
                </h4>
                <p className="text-sm text-gray-600 ml-6">
                  All rentals include basic liability insurance at no extra cost. This covers third-party damages up to state minimum requirements.
                </p>
              </div>

              <div>
                <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                  <i className="ri-check-line text-teal-600"></i>
                  Collision Damage Waiver (Optional)
                </h4>
                <p className="text-sm text-gray-600 ml-6">
                  Available for an additional $50/week. Reduces your liability for vehicle damage in case of accidents.
                </p>
              </div>

              <div>
                <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                  <i className="ri-check-line text-teal-600"></i>
                  Personal Insurance
                </h4>
                <p className="text-sm text-gray-600 ml-6">
                  You may use your own auto insurance policy if it covers rental vehicles. Please verify with your insurance provider.
                </p>
              </div>

              <div className="bg-white rounded-lg p-4 mt-6">
                <p className="text-xs text-gray-600">
                  <strong className="text-gray-900">Important:</strong> A first week's rental fee in advance will act as security deposit of $250 is required. In case of extensive damage, no refund will be submitted and damages will be claimed off the renter's insurance policy.
                </p>
              </div>
            </div>
          </div>

          {/* Requirements */}
          <div className="bg-gray-50 rounded-2xl p-8 md:p-10">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-14 h-14 flex items-center justify-center bg-gray-900 rounded-lg">
                <i className="ri-file-list-3-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900">Rental Requirements</h3>
            </div>

            <div className="space-y-5">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 flex items-center justify-center bg-teal-100 rounded-lg flex-shrink-0">
                  <i className="ri-user-line text-xl text-teal-600"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Age Requirement</h4>
                  <p className="text-sm text-gray-600">Must be at least 23 years old with a valid driver's license</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-10 h-10 flex items-center justify-center bg-teal-100 rounded-lg flex-shrink-0">
                  <i className="ri-bank-card-line text-xl text-teal-600"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Valid Driver's License</h4>
                  <p className="text-sm text-gray-600">Current, unexpired license held for at least 1 year</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-10 h-10 flex items-center justify-center bg-teal-100 rounded-lg flex-shrink-0">
                  <i className="ri-bank-card-2-line text-xl text-teal-600"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Payment Method</h4>
                  <p className="text-sm text-gray-600">Credit or debit card in your name for payment and deposit</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-10 h-10 flex items-center justify-center bg-teal-100 rounded-lg flex-shrink-0">
                  <i className="ri-file-text-line text-xl text-teal-600"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Proof of Address</h4>
                  <p className="text-sm text-gray-600">Recent utility bill or bank statement (within 90 days)</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-10 h-10 flex items-center justify-center bg-teal-100 rounded-lg flex-shrink-0">
                  <i className="ri-shield-user-line text-xl text-teal-600"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Clean Driving Record</h4>
                  <p className="text-sm text-gray-600">No major violations or DUI in the past 3 years</p>
                </div>
              </div>

              <div className="bg-white rounded-lg p-4 mt-6">
                <p className="text-xs text-gray-600">
                  <strong className="text-gray-900">Note:</strong> Additional verification may be required for first-time renters. We aim to make the process as quick and easy as possible.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Additional Information */}
        <div className="max-w-6xl mx-auto mt-12">
          <div className="bg-gradient-to-r from-teal-600 to-cyan-600 rounded-2xl p-8 md:p-10 text-white">
            <h3 className="text-2xl font-bold mb-6 text-center">What Happens at Pick-Up?</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-white/20 backdrop-blur-sm rounded-full mx-auto mb-3">
                  <span className="text-2xl font-bold">1</span>
                </div>
                <h4 className="font-semibold mb-2">Document Review</h4>
                <p className="text-sm text-white/90">We verify your license, insurance, and payment method</p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-white/20 backdrop-blur-sm rounded-full mx-auto mb-3">
                  <span className="text-2xl font-bold">2</span>
                </div>
                <h4 className="font-semibold mb-2">Vehicle Inspection</h4>
                <p className="text-sm text-white/90">Walk around the car together to note any existing damage</p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-white/20 backdrop-blur-sm rounded-full mx-auto mb-3">
                  <span className="text-2xl font-bold">3</span>
                </div>
                <h4 className="font-semibold mb-2">Sign Agreement</h4>
                <p className="text-sm text-white/90">Review and sign the rental contract with clear terms</p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-white/20 backdrop-blur-sm rounded-full mx-auto mb-3">
                  <span className="text-2xl font-bold">4</span>
                </div>
                <h4 className="font-semibold mb-2">Hit the Road</h4>
                <p className="text-sm text-white/90">Get your keys and start earning with your rental</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}